// «Мои повторы»: the rows of the replay list as plain html (frontScreens.ts mounts it), plus the one guess the
// client can make about which seat was this browser's player — the list only holds the player's own
// matches, so a roster name equal to the stored name, when exactly one matches, is theirs.
import type { Team } from '../../core/layout.js';
import type { ReplayRosterEntry, ReplaySummary } from '../../shared/replay/format.js';
import { escapeHtml, playerLabel } from '../nameStore.js';
import { getLang, t } from '../i18n/index.js';
import { teamName, heroName } from '../i18n/names.js';
import type { HeroKind } from '../../core/heroes.js';
import { HEROES } from '../../core/heroes.js';
import { mmss } from './playback.js';

/** The seat whose name is `name`, when exactly one is; null otherwise (no name, bots only, a namesake). */
export function viewerSeatOf(roster: readonly ReplayRosterEntry[], name: string | null): number | null {
  const want = name?.trim();
  if (!want) return null;
  const hits = roster.flatMap((r, i) => (r.name === want ? [i] : []));
  return hits.length === 1 ? hits[0]! : null;
}

/** «Победа» / «Поражение» for the player's side when known, otherwise who won. */
export function resultText(s: Pick<ReplaySummary, 'heroes' | 'winner' | 'aborted'>, name: string | null): { text: string; tone: 'win' | 'loss' | 'none' } {
  if (s.aborted || !s.winner) return { text: t('replay.aborted'), tone: 'none' };
  const seat = viewerSeatOf(s.heroes, name);
  if (seat === null) return { text: t('replay.teamWon', { team: teamName(s.winner) }), tone: 'none' };
  return s.heroes[seat]!.team === s.winner ? { text: t('res.victory'), tone: 'win' } : { text: t('res.defeat'), tone: 'loss' };
}

const two = (n: number) => String(n).padStart(2, '0');
/** The UTC timestamp in the browser's own time zone: «26 сентября, 18:04» / "26 September, 18:04". */
export function localDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString(getLang() === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'long' });
  return `${day}, ${two(d.getHours())}:${two(d.getMinutes())}`;
}

function teamLine(heroes: readonly ReplayRosterEntry[], team: Team, mine: number | null): string {
  const who = heroes.flatMap((h, i) => h.team !== team ? [] : [
    `<span class="rl-hero${i === mine ? ' me' : ''}">${escapeHtml(h.kind in HEROES ? heroName(h.kind as HeroKind) : h.kind)} <small>${h.name === null ? t('replay.bot') : escapeHtml(playerLabel(h.name))}</small></span>`,
  ]);
  return `<div class="rl-team ${team}"><b>${teamName(team)}</b> ${who.join(', ')}</div>`;
}

export function replayRowHtml(s: ReplaySummary, name: string | null): string {
  const mine = viewerSeatOf(s.heroes, name), res = resultText(s, name);
  return `
    <li><button type="button" class="rl-row" data-replay="${escapeHtml(s.id)}">
      <span class="rl-when">${localDateTime(s.startedAt)}</span>
      <span class="rl-result ${res.tone}">${res.text} <em>${s.score.a} : ${s.score.b}</em></span>
      <span class="rl-dur">${mmss(s.durationSec)}</span>
      ${teamLine(s.heroes, 'a', mine)}${teamLine(s.heroes, 'b', mine)}
    </button></li>`;
}

export function replayListHtml(replays: readonly ReplaySummary[], name: string | null): string {
  if (!replays.length) return `<p class="front-note rl-empty">${t('replay.empty')}</p>`;
  return `<ul class="rl-list">${replays.map(s => replayRowHtml(s, name)).join('')}</ul>`;
}
